import React, { useState, useEffect, useMemo } from 'react';
import { fetchAccessPointReport } from '../services/api';
import { toTitleCase } from '../utils/format';
import AccessPointPickups from './AccessPointPickups';

// UPS Access Point customer report (Rachael, 2026-10-12). One row per order that
// chose an access point at checkout: the store it went to, what the customer said
// in the post-delivery survey, and any problem notes on the order. The query is
// `access_point_report` in netlify/functions/neon.js. The Pickup Queue tab is
// AccessPointPickups.js (Greg, 2026-10-13).

const day = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const num = (v) => { const n = parseInt(v, 10); return isFinite(n) && n > 0 ? n : null; };
const csvCell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;

const RATINGS = [['over_rate', 'Overall'], ['deliv_rate', 'Delivery'], ['prod_rate', 'Product'],
  ['pack_rate', 'Packaging'], ['serv_rate', 'Service'], ['ease_rate', 'Ease'], ['like_rate', 'Recommend']];

function Score({ label, value }) {
  const n = num(value);
  if (n == null) return null;
  const color = n <= 2 ? 'var(--red)' : n === 3 ? 'var(--orange)' : 'var(--green)';
  return <span style={{ marginRight: 10, whiteSpace: 'nowrap' }}>{label} <strong style={{ color }}>{n}</strong></span>;
}

export default function AccessPointReport({ navigateTo }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [only, setOnly] = useState('all');
  const [tab, setTab] = useState('pickups');
  // Loaded on first visit to the tab: it reads BigCommerce once per order.
  useEffect(() => {
    if (tab === 'orders' && !rows && !err) fetchAccessPointReport().then(setRows).catch(e => setErr(e.message));
  }, [tab, rows, err]);

  const live = useMemo(() => (rows || []).filter(r => r.bc_status !== 'Cancelled' && r.status_id !== 5), [rows]);
  const hasFeedback = (r) => Boolean(r.feed_date);
  const hasProblem = (r) => Array.isArray(r.problems) && r.problems.length > 0;
  const list = live.filter(r => only === 'all' || (only === 'feedback' && hasFeedback(r)) || (only === 'problems' && hasProblem(r))
    || (only === 'either' && (hasFeedback(r) || hasProblem(r))));

  const replies = live.filter(hasFeedback);
  const delivScores = replies.map(r => num(r.deliv_rate)).filter(n => n != null);
  const avgDeliv = delivScores.length ? (delivScores.reduce((a, b) => a + b, 0) / delivScores.length).toFixed(1) : null;
  const unreadable = live.filter(r => r.problems === null).length;

  const exportCsv = () => {
    const head = ['Order', 'Order date', 'Ship date', 'Customer', 'Store', 'Store address', 'Access Point ID', 'Ship method', 'Tracking',
      'Order status', 'Survey date', ...RATINGS.map(x => x[1] + ' (1-5)'), 'Survey comment', 'Problem notes'];
    const body = list.map(r => [r.bc_order_id, day(r.order_created), day(r.ship_date), r.customer, r.store, r.store_address, r.access_point_id,
      r.ship_method, r.tracking, r.bc_status, day(r.feed_date), ...RATINGS.map(x => num(r[x[0]]) ?? ''), r.comments,
      (r.problems || []).map(p => [p.date, p.by, p.type, p.description, p.resolution ? 'Resolution: ' + p.resolution : ''].filter(Boolean).join(' | ')).join(' || ')]);
    const csv = [head, ...body].map(line => line.map(csvCell).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv' }));
    a.download = `ups-access-point-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const tile = (label, value, sub) => (
    <div style={{ flex: '1 1 150px', background: 'white', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
  const th = { textAlign: 'left', padding: '8px 10px', color: 'var(--gray-500)', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', verticalAlign: 'top' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--navy)' }}>UPS Access Point Orders</h2>
          {tab === 'orders' && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
            Every order that chose a UPS Access Point at checkout since the feature went live on Sep 23, 2026,
            with the customer's survey reply and any problem notes on the order.
          </div>}
        </div>
        {tab === 'orders' && <button className="btn btn-navy" onClick={exportCsv} disabled={!list.length}>Export CSV</button>}
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
        {[['pickups', 'Pickup Queue'], ['orders', 'Orders & Feedback']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ fontSize: 11, padding: '6px 16px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontWeight: 700, background: tab === k ? 'var(--navy)' : 'var(--gray-200)', color: tab === k ? '#fff' : 'var(--gray-600)' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'pickups' && <AccessPointPickups navigateTo={navigateTo} />}

      {tab === 'orders' && err && <div className="card" style={{ padding: 16, color: 'var(--red)', fontSize: 13 }}>Error: {err}</div>}
      {tab === 'orders' && !rows && !err && <div className="card" style={{ padding: 16, color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}

      {tab === 'orders' && rows && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {tile('Orders', live.length, rows.length > live.length ? `${rows.length - live.length} cancelled not shown` : null)}
            {tile('Survey replies', replies.length, live.length ? `${Math.round(100 * replies.length / live.length)}% of orders` : null)}
            {tile('Delivery rating', avgDeliv ?? '—', avgDeliv ? `average of ${delivScores.length}, out of 5` : 'no replies yet')}
            {tile('Orders with problem notes', live.filter(hasProblem).length, unreadable ? `${unreadable} could not be read` : null)}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, borderBottom: '1px solid var(--gray-200)' }}>
              <span style={{ color: 'var(--gray-500)' }}>Show</span>
              {[['all', 'All orders'], ['either', 'Feedback or a problem'], ['feedback', 'Survey replies'], ['problems', 'Problem notes']].map(([k, label]) => (
                <button key={k} onClick={() => setOnly(k)} className={only === k ? 'btn btn-primary' : 'btn'} style={{ fontSize: 11 }}>{label}</button>
              ))}
              <span style={{ marginLeft: 'auto', color: 'var(--gray-500)' }}>{list.length} shown</span>
            </div>
            {list.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Nothing to show.</div>}
            {list.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={th}>Order</th>
                      <th style={th}>Access Point</th>
                      <th style={th}>Shipped</th>
                      <th style={{ ...th, minWidth: 260 }}>Survey feedback</th>
                      <th style={{ ...th, minWidth: 280 }}>Problem notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(r => (
                      <tr key={r.bc_order_id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                        <td style={td}>
                          <span style={{ color: 'var(--orange)', fontWeight: 600, cursor: 'pointer' }}
                            onClick={() => navigateTo?.('order-details', r.bc_order_id)}>{r.bc_order_id}</span>
                          <div style={{ color: 'var(--navy)' }}>{toTitleCase(r.customer || '')}</div>
                          <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>Ordered {day(r.order_created)}</div>
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.store || 'Store name not on the order'}</div>
                          <div style={{ color: 'var(--gray-600)' }}>{r.store_address}</div>
                          <div style={{ color: 'var(--gray-400)', fontSize: 11 }}>ID {r.access_point_id}</div>
                        </td>
                        <td style={td}>
                          <div>{r.ship_date ? day(r.ship_date) : (r.bc_status || 'Not yet')}</div>
                          <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{r.ship_method}</div>
                          {r.tracking && (
                            <a href={`https://www.ups.com/track?tracknum=${encodeURIComponent(r.tracking)}`} target="_blank" rel="noopener noreferrer"
                              style={{ color: 'var(--orange)', fontSize: 11 }}>{r.tracking}</a>
                          )}
                        </td>
                        <td style={td}>
                          {hasFeedback(r) ? (
                            <>
                              <div style={{ fontSize: 11.5 }}>{RATINGS.map(([k, label]) => <Score key={k} label={label} value={r[k]} />)}</div>
                              {r.comments && <div style={{ marginTop: 5, color: 'var(--navy)', fontStyle: 'italic' }}>"{r.comments}"</div>}
                              <div style={{ color: 'var(--gray-400)', fontSize: 11, marginTop: 3 }}>Replied {day(r.feed_date)}</div>
                            </>
                          ) : <span style={{ color: 'var(--gray-400)' }}>No reply yet</span>}
                        </td>
                        <td style={td}>
                          {r.problems === null && <span style={{ color: 'var(--orange)' }}>Could not read the notes. Open the order.</span>}
                          {Array.isArray(r.problems) && r.problems.length === 0 && <span style={{ color: 'var(--gray-400)' }}>None</span>}
                          {(r.problems || []).map((p, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                              <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{p.type || 'Note'}
                                <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 11 }}>{[p.date && ` · ${day(new Date(p.date))}`, p.by && ` · ${p.by}`].filter(Boolean).join('')}</span>
                              </div>
                              <div>{p.description}</div>
                              {p.resolution && <div style={{ color: 'var(--gray-600)' }}><em>Resolution:</em> {p.resolution}</div>}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
